import { getCurrentLanguage } from '@/js/helper/locale.ts';

export const RECAPTCHA_FIELD_NAME = 'y-smart-captcha-response';
export const CAPTCHA_CONTAINER_SELECTOR = '.js-captcha-container';

const metaElement = document.querySelector<HTMLMetaElement>('meta[name="recaptcha-site-key"]');
export function getRecaptchaKey(): string {
  return metaElement?.content ?? '';
}

type SmartCaptchaRenderOptions = {
  sitekey: string
  hl?: string
  invisible?: boolean
};

type SmartCaptchaApi = {
  render: (element: HTMLElement, options: SmartCaptchaRenderOptions) => number
  reset: (widgetId: number) => void
  execute: (widgetId: number) => void
  subscribe: (widgetId: number, event: 'success' | 'token-expired', callback: (token?: string) => void) => void
};

function getSmartCaptchaApi(): SmartCaptchaApi | undefined {
  return (window as unknown as { smartCaptcha?: SmartCaptchaApi }).smartCaptcha;
}

export class Captcha {
  private token: string | null = null;
  private widgetId: number | null = null;
  private readonly lang: string;
  private readonly key: string;
  private readonly captchaElement: HTMLElement;
  private static scriptLoadPromise: Promise<void> | null = null;

  constructor(
    private container: HTMLElement,
  ) {
    this.lang = getCurrentLanguage();
    this.key = getRecaptchaKey();
    this.captchaElement = document.createElement('div');
    this.container.appendChild(this.captchaElement);
  }

  private static async loadSmartCaptchaScriptOnce(): Promise<void> {
    const src = 'https://smartcaptcha.yandexcloud.net/captcha.js';
    if (this.scriptLoadPromise) {
      await this.scriptLoadPromise;
      return;
    }

    const alreadyPresent = Array.from(document.scripts).some(s => s.src === src);
    if (alreadyPresent) {
      this.scriptLoadPromise = Promise.resolve();
      await this.scriptLoadPromise;
      return;
    }

    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Yandex SmartCaptcha'));
      document.head.appendChild(script);
    });
    await this.scriptLoadPromise;
  }

  private async ensureWidget(): Promise<void> {
    await Captcha.loadSmartCaptchaScriptOnce();
    const api = getSmartCaptchaApi();
    if (!api) {
      throw new TypeError('SmartCaptcha is not available after loading');
    }
    if (this.widgetId !== null) {
      return;
    }

    this.widgetId = api.render(this.captchaElement, {
      sitekey: this.key,
      hl: this.lang,
      invisible: true,
    });
  }

  render() {
    void this.ensureWidget();
  }

  getToken() {
    return this.token;
  }

  reset() {
    this.token = null;
    if (this.widgetId !== null) {
      const api = getSmartCaptchaApi();
      if (api) {
        api.reset(this.widgetId);
      }
      this.widgetId = null;
    }
    this.container.removeChild(this.captchaElement);
  }

  async execute(): Promise<string> {
    await this.ensureWidget();
    return new Promise((resolve, reject) => {
      if (this.widgetId === null) {
        reject(new Error('Captcha widget not initialized'));
        return;
      }

      const api = getSmartCaptchaApi();
      if (!api) {
        reject(new TypeError('SmartCaptcha is not available'));
        return;
      }
      api.execute(this.widgetId);

      const onToken = (token?: string) => {
        if (token == null || token === '') {
          reject(new TypeError('Captcha token missing'));
          return;
        }
        this.token = token;
        resolve(token);
      };

      const onExpired = () => {
        this.token = null;
        reject(new Error('Captcha expired'));
      };

      api.subscribe(this.widgetId, 'success', onToken);
      api.subscribe(this.widgetId, 'token-expired', onExpired);
    });
  }
}
